import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Globe2, Menu, X } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { BRAND } from "../../constants/site";
import logoDark from "../../assets/logo_dark.png";
import { navLinks, studyDestinations } from "../../data/homeData";
import siteConfig from "../../data/siteConfig.json";
import CountryFlagIcon from "../common/CountryFlagIcon";

function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isStudyDropdownOpen, setIsStudyDropdownOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const allowTransparentNav =
    location.pathname === "/" || location.pathname.startsWith("/study-in/");
  const isInnerPage = !allowTransparentNav;
  const useSolidNav = isScrolled || !allowTransparentNav;
  const useWhiteCta =
    location.pathname === "/about"
    || location.pathname === "/testimonials"
    || location.pathname === "/contact";
  const isStudyActive = location.pathname.startsWith("/study-in/");
  const desktopLinkClass = ({ isActive }) =>
    `text-sm font-medium transition-colors ${
      isActive
        ? "text-warm-gold"
        : "text-midnight-navy/80 hover:text-midnight-navy"
    }`;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 28);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const openStudyDropdown = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    setIsStudyDropdownOpen(true);
  };

  const closeStudyDropdown = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsStudyDropdownOpen(false);
    }, 120);
  };

  useEffect(() => {
    setIsMobileOpen(false);
    setIsStudyDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStudyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        useSolidNav ? "px-3 pt-3" : "px-3 pt-5"
      }`}
    >
      <nav
        className={`mx-auto flex w-full max-w-7xl items-center justify-between rounded-full border px-5 py-3.5 md:px-7 transition-all duration-300 ${
          isInnerPage || useSolidNav
            ? "border-midnight-navy/5 bg-white shadow-[0_8px_32px_-12px_rgba(7,18,38,0.08)] backdrop-blur-sm"
            : "border-white/20 bg-white/30 backdrop-blur-md"
        }`}
      >
        <Link to="/" className="group flex items-center">
          <img
            src={logoDark}
            alt={BRAND.name}
            className="h-9 md:h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
          />
        </Link>
        <div className="hidden items-center gap-8 lg:flex">
          <NavLink className={desktopLinkClass} to="/">
            Home
          </NavLink>

          <div
            className="relative"
            onMouseEnter={openStudyDropdown}
            onMouseLeave={closeStudyDropdown}
            ref={dropdownRef}
          >
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 text-sm font-semibold transition-colors ${
                isStudyActive
                  ? "text-warm-gold"
                  : "text-midnight-navy/80 hover:text-midnight-navy"
              }`}
              aria-expanded={isStudyDropdownOpen}
              aria-haspopup="menu"
              onClick={() => {
                if (closeTimeoutRef.current) {
                  clearTimeout(closeTimeoutRef.current);
                }
                setIsStudyDropdownOpen((prev) => !prev);
              }}
            >
              Study In
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isStudyDropdownOpen ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            <AnimatePresence>
              {isStudyDropdownOpen ? (
                <motion.div
                  initial={{ opacity: 0, y: 15, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="absolute left-1/2 top-full z-50 mt-4 w-[460px] -translate-x-1/2 rounded-[1.5rem] border border-midnight-navy/5 bg-white p-6 shadow-[0_24px_48px_-16px_rgba(7,18,38,0.15)]"
                  role="menu"
                  onMouseEnter={openStudyDropdown}
                  onMouseLeave={closeStudyDropdown}
                >
                  <div className="mb-4 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-midnight-navy">
                    <Globe2 className="h-4 w-4 text-royal-blue" />
                    Explore Destinations
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {studyDestinations.map((country) => (
                      <Link
                        key={country.slug}
                        className="group flex items-center rounded-xl border border-midnight-navy/5 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-text transition-all hover:border-royal-blue/30 hover:bg-royal-blue/5 hover:text-royal-blue"
                        to={`/study-in/${country.slug}`}
                      >
                        <CountryFlagIcon
                          slug={country.slug}
                          name={country.name}
                          width={20}
                          height={14}
                          className="mr-3 transition-transform group-hover:scale-110"
                        />
                        {country.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>

            {isStudyDropdownOpen ? <div className="absolute left-0 right-0 top-full h-4" aria-hidden="true" /> : null}
          </div>

          {navLinks
            .filter((item) => item.label !== "Home")
            .map((item) => (
              <NavLink key={item.to} className={desktopLinkClass} to={item.to}>
                {item.label}
              </NavLink>
            ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link
            to={siteConfig.links.bookCounselling}
            data-flight-message="Book your free counselling session now."
            className={`rounded-xl px-5 py-2.5 text-sm font-semibold shadow-soft ${
              useWhiteCta
                ? "border border-midnight-navy/15 bg-white text-midnight-navy hover:bg-sky-glow"
                : "bg-midnight-navy text-soft-white hover:bg-royal-blue"
            }`}
          >
            Book Free Counselling
          </Link>
        </div>

        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-midnight-navy/10 bg-white text-midnight-navy lg:hidden"
          onClick={() => setIsMobileOpen((prev) => !prev)}
          aria-expanded={isMobileOpen}
          aria-label="Toggle navigation menu"
        >
          {isMobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {isMobileOpen ? (
          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -18 }}
            transition={{ duration: 0.25 }}
            className="mx-auto mt-3 w-full max-w-7xl rounded-2xl border border-white/35 bg-white/92 p-4 shadow-luxury backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-2">
              {navLinks.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `block rounded-xl px-3 py-2 text-sm font-medium ${
                      isActive
                        ? "bg-warm-gold/14 text-warm-gold"
                        : "text-midnight-navy hover:bg-midnight-navy/5"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-midnight-navy/10 bg-sky-glow/50 p-3">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-midnight-navy/70">
                Study In
              </p>
              <div className="grid grid-cols-2 gap-2">
                {studyDestinations.map((country) => (
                  <Link
                    key={country.slug}
                    className="rounded-xl bg-white px-3 py-2 text-sm font-medium text-midnight-navy shadow-sm"
                    to={`/study-in/${country.slug}`}
                  >
                    <CountryFlagIcon slug={country.slug} name={country.name} width={18} height={12} className="mr-2" />
                    {country.name}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to={siteConfig.links.bookCounselling}
              data-flight-message="Book your free counselling session now."
              className={`mt-4 block rounded-xl px-4 py-3 text-center text-sm font-semibold ${
                useWhiteCta
                  ? "border border-midnight-navy/15 bg-white text-midnight-navy"
                  : "bg-midnight-navy text-soft-white"
              }`}
            >
              Book Free Counselling
            </Link>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}

export default Navbar;
